/**
 * LegalAcceptanceCheckbox — drop-in component for signup / expert-application
 * / booking flows.
 *
 * Renders a checkbox with inline links to the linked policy documents.
 * On submit (handled by the parent form), the parent calls
 * `useLegalAcceptance().recordAll()` to persist each acceptance to
 * /api/legal/acceptances with the user's auth token.
 *
 * Why a hook + a presentational component?
 *   - the parent owns "should we even enable submit yet?" via `checked` state
 *   - the parent owns when to actually POST (typically AFTER user creation,
 *     because acceptances FK to users.id) and MUST handle a failed POST by
 *     blocking the user, never by logging it (see recordAll below)
 *
 * Usage:
 *
 *   const [acceptedToS, setAcceptedToS] = useState(false);
 *   const { recordAll } = useLegalAcceptance(['terms_of_service', 'privacy_policy']);
 *
 *   <LegalAcceptanceCheckbox
 *     policies={[
 *       { id: 'terms_of_service',  label: 'Terms of Service',  href: '/terms' },
 *       { id: 'privacy_policy',    label: 'Privacy Policy',    href: '/privacy' },
 *     ]}
 *     checked={acceptedToS}
 *     onChange={setAcceptedToS}
 *   />
 *
 *   // After successful signup:
 *   await recordAll();
 */
import { useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { POLICY_VERSION, recordAcceptances } from "@/lib/legalAcceptance";


// The text hash we send is the SHA-256 of the exact policy text bundled in
// this build (src/legal-rendered/<policy_id>.js, generated by
// bin/extract_legal_docs.py). The backend stores what we send. It equals
// legal_audit.policy_versions.text_hash for the same policy, and
// lib/legalPolicyContract.test.js fails CI if the two ever drift.
//
// If you change a policy:
//   1. Re-run `python bin/extract_legal_docs.py`
//   2. Add a new policy_versions row (a migration; never UPDATE in place) and
//      bump POLICY_VERSION in lib/legalAcceptance.js to match.

// Resolved by webpack as a context module over src/legal-rendered/.
const loadPolicyText = async (policyId) =>
  (await import(`@/legal-rendered/${policyId}`)).default;


/**
 * useLegalAcceptance(policyIds, policyVersion = POLICY_VERSION)
 *
 * Returns { recordAll }. recordAll([token]) POSTs once per policy id and
 * RESOLVES ONLY IF EVERY ACCEPTANCE IS ON FILE; otherwise it throws a
 * LegalAcceptanceError (lib/legalAcceptance.js). Callers must handle it and
 * block the user; do not swallow it. Policies that already landed are
 * remembered, so calling recordAll again after a failure re-posts only the
 * missing ones.
 */
export function useLegalAcceptance(policyIds, policyVersion = POLICY_VERSION) {
  const recorded = useRef(new Set());

  const recordAll = useCallback(async (token) => {
    try {
      const res = await recordAcceptances({
        apiBase: API,
        policyIds,
        version: policyVersion,
        token,
        alreadyRecorded: [...recorded.current],
        loadPolicyText,
      });
      res.recorded.forEach((id) => recorded.current.add(id));
      return { recorded: res.recorded.length };
    } catch (err) {
      (err.recorded || []).forEach((id) => recorded.current.add(id));
      throw err;
    }
  }, [policyIds, policyVersion]);

  return { recordAll };
}


export default function LegalAcceptanceCheckbox({
  policies,
  checked,
  onChange,
  required = true,
  className = "",
}) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer ${className}`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        required={required}
        className="mt-1 w-4 h-4 accent-brand-green"
      />
      <span className="text-sm text-slateline-700 leading-relaxed">
        I have read and agree to the{" "}
        {policies.map((p, i) => (
          <span key={p.id}>
            <Link
              to={p.href}
              target="_blank"
              rel="noreferrer"
              className="text-brand-green underline hover:no-underline"
            >
              {p.label}
            </Link>
            {i < policies.length - 2 && ", "}
            {i === policies.length - 2 && policies.length > 1 && " and "}
          </span>
        ))}
        .
      </span>
    </label>
  );
}

export { LegalAcceptanceCheckbox };
